import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, ExpandableText, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { EventDetailGuestRow } from '@/features/guardian/components/event-detail/event-detail-guest-row';
import { EventDetailInfoRow } from '@/features/guardian/components/event-detail/event-detail-info-row';
import { EventDetailVenueRow } from '@/features/guardian/components/event-detail/event-detail-venue-row';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardian.kids.eventDetail;

type EventDetailInfoListProps = {
  event: FamilyEvent;
  dateLabel?: string;
  bookingMetaLabel?: string;
  onOpenMap?: () => void;
};

export function EventDetailInfoList({
  event,
  dateLabel,
  bookingMetaLabel,
  onOpenMap,
}: EventDetailInfoListProps) {
  const { isTablet, scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack gap={scale(16)} style={[styles.card, !isTablet ? styles.mobileShadow : null]}>
      <EventDetailGuestRow event={event} />
      <EventDetailVenueRow venue={event.venue} onOpenMap={onOpenMap} />

      {event.coach ? (
        <EventDetailInfoRow icon="IcBook" label={strings.coachLead}>
          <Stack direction="row" align="center" gap={scale(8)}>
            <Avatar size={scale(24)} borderWidth={0} activeOpacity={1}>
              <Avatar.Image
                source={event.coach.avatar ?? imgUser}
                backgroundColor={guardianColors['2']}
              />
            </Avatar>
            <Text
              numberOfLines={2}
              variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
              style={styles.primaryText}>
              {event.coach.name}
            </Text>
          </Stack>
        </EventDetailInfoRow>
      ) : null}

      {dateLabel ? (
        <EventDetailInfoRow icon="IcCalendarFill">
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
            style={styles.primaryText}>
            {dateLabel}
          </Text>
        </EventDetailInfoRow>
      ) : null}

      <EventDetailInfoRow icon="IcClockFill">
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.primaryText}>
          {event.timeRangeLabel}
        </Text>
      </EventDetailInfoRow>

      {bookingMetaLabel ? (
        <EventDetailInfoRow icon="IcUserTick">
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
            style={styles.primaryText}>
            {bookingMetaLabel}
          </Text>
        </EventDetailInfoRow>
      ) : null}

      {event.description ? (
        <EventDetailInfoRow icon="IcNote">
          <ExpandableText
            collapsedLines={4}
            variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
            textStyle={styles.description}
            toggleStyle={styles.expandToggle}>
            {event.description}
          </ExpandableText>
        </EventDetailInfoRow>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    padding: scale(12),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
  },
  mobileShadow: {
    boxShadow: '0px 1px 3px #D5D9E24D',
  },
  primaryText: {
    color: guardianColors['10'],
  },
  description: {
    color: guardianColors['10'],
  },
  expandToggle: {
    color: guardianColors['Main/Main'],
  },
}));
